import { Box, Button, Card, Typography, useTheme } from '@mui/material';
import { AxiosError } from 'axios';
import { BenefitDTOBenefitEnum, BikeleasingV2BenefitDetailsDTO } from 'probonio-shared-ui/api';
import { BikeleasingBIcon } from 'probonio-shared-ui/component/icon';
import { CircularProgressBar } from 'probonio-shared-ui/component/progress';
import { apis, useEmployeeQuery } from 'probonio-shared-ui/module/api';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { BackIconButton } from '../../component/button/BackIconButton';
import { MobileLayout } from '../../component/layout';
import { AppToolbar } from '../../component/layout/AppToolbar';
import { useActiveBenefitsQuery } from '../../module/benefit/useActiveBenefitsQuery';
import { BikeleasingContractDetails } from '../../module/bikeleasingV2/BikeleasingContractDetails';
import { HighlightGuideButton } from '../../module/guiding';
import { useBackNavigator } from '../../module/navigation/BackNavigator';

export const BikeleasingV2ContractDetailsPage: React.FC = () => {
  const { contractId } = useParams();
  const { back } = useBackNavigator();
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: activeBenefits } = useActiveBenefitsQuery();
  const { t } = useTranslation('benefitModule');
  const benefit = BenefitDTOBenefitEnum.BikeleasingV2.toLowerCase();

  const bikeleasingBenefitDetails = useMemo(
    () =>
      activeBenefits?.benefits.find(activeBenefit => activeBenefit.benefit === BenefitDTOBenefitEnum.BikeleasingV2)?.details as
        BikeleasingV2BenefitDetailsDTO | undefined,
    [activeBenefits?.benefits],
  );

  const { data: bikeleasingContract, isError } = useEmployeeQuery(
    ['benefit', 'bikeleasingV2', 'contract', contractId!],
    async params => {
      try {
        const res = await apis.employee.getBikeleasingV2ContractById({ ...params, contractId: contractId! });
        return res.data;
      } catch (err) {
        // bikeleasing may list a contract it cannot return in detail - resolve to null instead of an error,
        // so neither the global error message nor retries kick in
        if (err instanceof AxiosError && err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    { enabled: !!contractId },
  );

  const content = useMemo(() => {
    // a failed reload keeps the previously loaded contract, so only show the error when there is nothing to display
    if (bikeleasingContract === null || (isError && bikeleasingContract === undefined)) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 2, textAlign: 'center' }}>
          <Typography variant="body1">
            {bikeleasingContract === null
              ? t('extensions:bikeleasing_v2.contract.notAvailable')
              : t('extensions:bikeleasing_v2.contract.loadError')}
          </Typography>
          <Button variant="contained" onClick={() => navigate(`/${benefit}`, { replace: true })}>
            {t('extensions:bikeleasing_v2.contract.backToContracts')}
          </Button>
        </Box>
      );
    }
    if (bikeleasingContract && bikeleasingBenefitDetails?.bikeleasingCondition) {
      return <BikeleasingContractDetails condition={bikeleasingBenefitDetails.bikeleasingCondition} contract={bikeleasingContract} />;
    }
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgressBar />
      </Box>
    );
  }, [benefit, bikeleasingBenefitDetails, bikeleasingContract, isError, navigate, t]);

  useEffect(() => {
    if (!contractId) {
      back();
    }
  }, [back, contractId]);

  return (
    <MobileLayout
      iosOverloadScrollBackground={theme.palette.background.paper}
      header={
        <Card sx={{ borderRadius: 0, boxShadow: 0 }}>
          <AppToolbar
            title={
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <BikeleasingBIcon fontSize="extraLarge" />
                <Typography variant="extraBig">{t('benefit.BIKELEASING_V2.name')}</Typography>
              </Box>
            }
            backButton={<BackIconButton onClick={back} />}
            actionButton={
              <HighlightGuideButton
                name={t('benefit.BIKELEASING_V2.name')}
                header={t('extensions:bikeleasing_v2.firstHelpGuide.header')}
                text={t('extensions:bikeleasing_v2.firstHelpGuide.text')}
                onClick={() => navigate(`/${benefit}/info`)}
              />
            }
          />
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              mb: 0.5,
            }}
          >
            {t('extensions:bikeleasing_v2.contract.contractNumber', { contractNumber: bikeleasingContract?.contractNumber ?? '-' })}
          </Typography>
        </Card>
      }
    >
      {content}
    </MobileLayout>
  );
};
